import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  Alert,
  Modal,
  Platform,
  ActivityIndicator,
} from 'react-native';
import ModernButton from '../components/ModernButton';
import ModernCard from '../components/ModernCard';
import { AuthManager } from '../utils/authManager';

// API base URL for web requests
const API_BASE_URL = process.env.REACT_APP_PRODUCT_SUBSCRIPTION_API_URL || 'https://bookapt-product-subscriptions.onrender.com';

// Conditional import for react-native-iap (mobile only) — business plans must use IAP only on mobile per App Store/Play policy
let RNIap: any = null;
let initConnection: any = null;
let purchaseErrorListener: any = null;
let purchaseUpdatedListener: any = null;
let getAvailablePurchases: any = null;
if (Platform.OS !== 'web') {
  try {
    const iapModule = require('react-native-iap');
    RNIap = iapModule.default ?? iapModule;
    initConnection = iapModule.initConnection;
    purchaseErrorListener = iapModule.purchaseErrorListener;
    purchaseUpdatedListener = iapModule.purchaseUpdatedListener;
    getAvailablePurchases = iapModule.getAvailablePurchases;
  } catch (e) {
    console.warn('react-native-iap not available:', e);
  }
}

interface PackageSelectionScreenProps {
  onSelect: (packageData: PackageData) => void;
  onBack: () => void;
}

interface PackageData {
  id: string;
  name: string;
  price: number;
  features: string[];
  popular?: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
  googleProductId?: string;
  appleProductId?: string;
}

const PackageSelectionScreen: React.FC<PackageSelectionScreenProps> = ({
  onSelect,
  onBack,
}) => {
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [businessOwnerId, setBusinessOwnerId] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [isWeb, setIsWeb] = useState(false);
  const [iapReady, setIapReady] = useState(false);
  const pendingSubscriptionPackageRef = useRef<PackageData | null>(null);

  // Initialize IAP store connection when screen mounts (required before any purchase)
  useEffect(() => {
    if (Platform.OS === 'web' || !initConnection) return;
    let cancelled = false;
    (async () => {
      try {
        await initConnection();
        if (!cancelled) setIapReady(true);
      } catch (e) {
        console.warn('IAP initConnection failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const packages: PackageData[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      stripeProductId: 'prod_TkGiPGU70g2111',
      stripePriceId: 'price_1SmmAzGAjH1SWAZvOf38Cg5C',
      googleProductId: 'com.bookapt.product.free',
      appleProductId: 'com.bookapt.product.free',
      features: [
        'Up to 10 active products',
        '2 product categories',
        '3 images per product',
        '2 variants per product',
        'Basic product management',
        'Inventory tracking',
        'Order management',
        'Payment processing',
        'Mobile app access',
      ],
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 29.99,
      stripeProductId: 'prod_TkGlw6fwvsVI8X',
      stripePriceId: 'price_1SmmDhGAjH1SWAZvUnyjU4Q3',
      googleProductId: 'com.bookapt.product.starter',
      appleProductId: 'com.bookapt.product.starter',
      features: [
        'Up to 25 active products',
        '3 product categories',
        '5 images per product',
        '3 variants per product',
        'Basic product management',
        'Inventory tracking',
        'Order management',
        'Basic analytics',
        'Email support (48-hour response)',
        'Payment processing',
        'Mobile app access',
      ],
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 79.99,
      stripeProductId: 'prod_TkGoBAAdNMG034',
      stripePriceId: 'price_1SmmGnGAjH1SWAZvmVK2W49w',
      googleProductId: 'com.bookapt.product.professional',
      appleProductId: 'com.bookapt.product.professional',
      features: [
        'Up to 100 active products',
        '10 product categories',
        '10 images per product',
        '10 variants per product',
        'Everything in Starter',
        'Advanced product management',
        'Bulk product import/export',
        'Advanced inventory tracking',
        'Order fulfillment tools',
        'Advanced analytics & reporting',
        'Priority support (24-hour response)',
        'Product promotion tools',
        'Discount/coupon management',
        'Multi-warehouse inventory',
        'API access',
      ],
      popular: true,
    },
    {
      id: 'business',
      name: 'Business',
      price: 149.99,
      stripeProductId: 'prod_TkGrROuVRYYXSY',
      stripePriceId: 'price_1SmmJuGAjH1SWAZvwYiEqL72',
      googleProductId: 'com.bookapt.product.business',
      appleProductId: 'com.bookapt.product.business',
      features: [
        'Up to 500 active products',
        'Unlimited categories',
        '20 images per product',
        '50 variants per product',
        'Everything in Professional',
        'Advanced product analytics',
        'Custom product fields',
        'Automated inventory alerts',
        'Multi-channel selling',
        'Advanced reporting & exports',
        'Priority phone support (12-hour response)',
        'Custom integrations',
        'Advanced promotion campaigns',
        'A/B testing for products',
        'Sales forecasting',
      ],
    },
  ];

  const handleSelectPackage = (packageId: string) => {
    setSelectedPackage(packageId);
  };

  useEffect(() => {
    setIsWeb(Platform.OS === 'web');
    
    // Get business owner ID
    const getBusinessOwnerId = async () => {
      try {
        const owner = await AuthManager.getSavedBusinessOwner();
        if (owner) {
          setBusinessOwnerId(owner.id || owner.email || '');
        }
      } catch (error) {
        console.error('Error getting business owner ID:', error);
      }
    };
    getBusinessOwnerId();

    // Check for Stripe Checkout success/cancel redirect (web only)
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const canceled = urlParams.get('canceled');
      const sessionId = urlParams.get('session_id');
      
      if (success === 'true' && sessionId) {
        // Verify payment actually succeeded before activating subscription
        setTimeout(async () => {
          try {
            // Verify the checkout session was actually paid
            const verifyResponse = await fetch(`${API_BASE_URL}/api/product-subscriptions/checkout/verify/${sessionId}`);
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              // Payment verified - subscription activated
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
              if (Platform.OS === 'web') {
                window.alert(`Success! Your ${verifyData.planName} subscription has been activated.`);
              } else {
                Alert.alert('Success', `Your ${verifyData.planName} subscription has been activated!`);
              }
              // Optionally call onSelect with the package data
              const packageData = packages.find(pkg => pkg.id === verifyData.planId);
              if (packageData && onSelect) {
                onSelect(packageData);
              }
            } else {
              // Payment not completed - show error
              console.error('Payment verification failed:', verifyData);
              window.history.replaceState({}, '', window.location.pathname);
              if (Platform.OS === 'web') {
                window.alert('Payment not completed. Please try again or contact support if you were charged.');
              } else {
                Alert.alert('Payment Error', 'Payment was not completed. Please try again.');
              }
            }
          } catch (error) {
            console.error('Error verifying payment:', error);
            // Clean URL anyway
            window.history.replaceState({}, '', window.location.pathname);
            if (Platform.OS === 'web') {
              window.alert('Unable to verify payment. Subscription will be activated automatically if payment was successful.');
            } else {
              Alert.alert('Verification Error', 'Unable to verify payment. Please check your subscription status.');
            }
          }
        }, 500);
      } else if (canceled === 'true') {
        // Payment canceled
        setTimeout(() => {
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        }, 500);
      }
    }
  }, []);

  // Mobile: IAP-only for paid plans (same as credits — App Store/Play policy). Listener handles success.
  const handleMobileSubscription = async (packageData: PackageData) => {
    if (!RNIap || Platform.OS === 'web') {
      Alert.alert(
        'In-App Purchase Required',
        'Subscription is only available via the App Store / Google Play. Please update the app from the store.'
      );
      return;
    }
    const sku = Platform.OS === 'ios' ? packageData.appleProductId : packageData.googleProductId;
    if (!sku) {
      Alert.alert('Error', 'This plan is not available for in-app purchase.');
      return;
    }
    try {
      setPurchasing(true);
      pendingSubscriptionPackageRef.current = packageData;
      // Subscription purchase (react-native-iap v14: request object with type 'subs')
      await RNIap.requestPurchase({
        request: {
          apple: { sku },
          google: { skus: [sku] },
        },
        type: 'subs',
      });
    } catch (error: any) {
      setPurchasing(false);
      pendingSubscriptionPackageRef.current = null;
      const msg = error?.message || '';
      const isStoreNotInit = /store connection not initialized|connection not initialized|not initialized/i.test(msg);
      Alert.alert(
        'Subscription Failed',
        isStoreNotInit
          ? 'Unable to connect to the store. Please try again or update the app from the App Store.'
          : msg || 'Failed to start subscription'
      );
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web' || !RNIap || !purchaseUpdatedListener || !purchaseErrorListener) return;
    const subUpdate = purchaseUpdatedListener((purchase: any) => {
      const pkg = pendingSubscriptionPackageRef.current;
      if (!pkg) return;
      const productId = purchase.productId ?? purchase.productID;
      const matches = productId === pkg.appleProductId || productId === pkg.googleProductId;
      if (!matches) return;
      (async () => {
        try {
          if (RNIap && RNIap.finishTransaction) await RNIap.finishTransaction({ purchase, isConsumable: false });
          pendingSubscriptionPackageRef.current = null;
          setPurchasing(false);
          Alert.alert('Success', `${pkg.name} subscription activated.`, [{ text: 'OK', onPress: () => onSelect(pkg) }]);
        } catch (e) {
          setPurchasing(false);
          Alert.alert('Error', 'Failed to complete subscription.');
        }
      })();
    });
    const subError = purchaseErrorListener((error: any) => {
      setPurchasing(false);
      pendingSubscriptionPackageRef.current = null;
      if (error?.code !== 'E_USER_CANCELLED') {
        const msg = error?.message || '';
        const isStoreNotInit = /store connection not initialized|connection not initialized|not initialized/i.test(msg);
        Alert.alert(
          'Subscription Error',
          isStoreNotInit
            ? 'Unable to connect to the store. Please try again or update the app from the App Store.'
            : msg || 'Purchase failed.'
        );
      }
    });
    return () => {
      try {
        subUpdate.remove();
        subError.remove();
      } catch (_) {}
    };
  }, [onSelect]);

  const handleRestorePurchases = async () => {
    if (!RNIap || Platform.OS === 'web' || !getAvailablePurchases) {
      if (Platform.OS !== 'web') {
        Alert.alert(
          'Restore Unavailable',
          'Subscription restore is not available. Please update the app from the App Store.'
        );
      }
      return;
    }
    try {
      setRestoring(true);
      const purchases = await getAvailablePurchases();
      const subscriptionProductIds = packages
        .filter(p => p.id !== 'free')
        .flatMap(p => [p.appleProductId, p.googleProductId].filter(Boolean));
      const subPurchases = Array.isArray(purchases)
        ? purchases.filter((p: any) => {
            const pid = p.productId || p.productID;
            return subscriptionProductIds.includes(pid);
          })
        : [];
      if (subPurchases.length > 0) {
        const productId = subPurchases[0].productId || subPurchases[0].productID;
        const matched = packages.find(
          p => p.appleProductId === productId || p.googleProductId === productId
        );
        if (matched) {
          Alert.alert(
            'Restore Complete',
            `Your ${matched.name} subscription has been restored.`,
            [{ text: 'OK', onPress: () => onSelect(matched) }]
          );
        } else {
          Alert.alert('Restore Complete', 'Your subscription has been restored.');
        }
      } else {
        Alert.alert('No Purchases to Restore', 'No previous subscription purchases were found for this account.');
      }
    } catch (error: any) {
      console.error('Restore error:', error);
      Alert.alert('Restore Failed', error?.message || 'Unable to restore purchases. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  const handleContinue = () => {
    if (!selectedPackage) return;

    const packageData = packages.find(pkg => pkg.id === selectedPackage);
    const isPaidPlan = packageData && packageData.id !== 'free';

    // Mobile paid plans: IAP only (policy — same as credits). No Stripe, no "Subscribe on Website" as primary.
    if (Platform.OS !== 'web' && isPaidPlan) {
      handleMobileSubscription(packageData!);
      return;
    }

    // Web, or mobile free plan: use existing flow (Stripe on web, free activation on mobile)
    // Only check hostname on web (window.location is undefined on React Native)
    const isLocalhost = Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      window.location &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (isLocalhost && API_BASE_URL.includes('localhost')) {
      if (Platform.OS === 'web') {
        window.alert(
          '🚫 Checkout Disabled in Local Development\n\n' +
          'Stripe checkout is disabled when running on localhost with local API to prevent test payments.\n\n' +
          'To test checkout:\n' +
          '1. Deploy to a staging/production environment\n' +
          '2. Or use Stripe test mode with proper CORS configuration\n\n' +
          'For now, you can test the plan selection flow, but checkout will be blocked.'
        );
      } else {
        Alert.alert(
          'Development Mode',
          'Checkout is disabled in local development to prevent test payments. Please use a staging or production environment to test checkout.',
          [{ text: 'OK' }]
        );
      }
      return;
    }

    handleWebPurchase();
  };


  const handleWebPurchase = async () => {
    const packageData = packages.find(pkg => pkg.id === selectedPackage);
    if (!packageData) {
      Alert.alert('Error', 'Please select a plan');
      return;
    }

    // Free plan - activate directly
    if (packageData.id === 'free') {
      try {
        setPurchasing(true);
        
        const owner = await AuthManager.getSavedBusinessOwner();
        if (!owner) {
          throw new Error('Business owner not found. Please log in again.');
        }

        const businessId = owner.id || owner.email || '';
        if (!businessId) {
          throw new Error('Unable to identify business owner');
        }

        // Activate free plan directly
        // Skip API call if running on localhost (CORS issues in development)
        if (typeof window !== 'undefined' && API_BASE_URL.includes('localhost')) {
          if (__DEV__) {
            console.warn('Skipping free plan activation API call on localhost due to CORS restrictions');
          }
          setPurchasing(false);
          // For free plan, we can still proceed locally
          if (Platform.OS === 'web') {
            window.alert('Free plan activation skipped in local development. In production, this would activate automatically.');
          } else {
            Alert.alert(
              'Development Mode',
              'Free plan activation skipped in local development. In production, this would activate automatically.',
              [{ text: 'OK', onPress: () => onSelect(packageData) }]
            );
            return;
          }
        }

        let response;
        try {
          response = await fetch(`${API_BASE_URL}/api/product-subscriptions/checkout/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              planId: 'free',
              businessId: businessId,
              businessEmail: owner.email || '',
              businessName: owner.name || owner.businessName || 'Business',
            }),
          });
        } catch (fetchError: any) {
          // Handle CORS and network errors gracefully
          console.error('Free plan activation fetch error:', fetchError);
          setPurchasing(false);
          
          if (fetchError.message?.includes('CORS') || fetchError.message?.includes('Failed to fetch')) {
            // For free plan, allow proceeding even if API fails (graceful degradation)
            if (Platform.OS === 'web') {
              const proceed = window.confirm('Unable to connect to subscription service. Would you like to proceed with the free plan anyway? (Subscription will be activated when the service is available)');
              if (proceed) {
                onSelect(packageData);
              }
            } else {
              Alert.alert(
                'Connection Error',
                'Unable to connect to subscription service. The free plan will be activated when the service is available.',
                [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Proceed', onPress: () => onSelect(packageData) }
                ]
              );
            }
          } else {
            if (Platform.OS === 'web') {
              window.alert(`Error: ${fetchError.message || 'Failed to activate plan'}`);
            } else {
              Alert.alert('Error', fetchError.message || 'Failed to activate plan');
            }
          }
          return;
        }

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText || 'Unknown error' };
          }
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.success) {
          if (Platform.OS === 'web') {
            window.alert('Free plan activated successfully!');
          } else {
            Alert.alert('Success', 'Free plan activated successfully!');
          }
          onSelect(packageData);
        } else {
          throw new Error(data.error || 'Failed to activate free plan');
        }
      } catch (error: any) {
        console.error('Error activating free plan:', error);
        setPurchasing(false);
        if (Platform.OS === 'web') {
          window.alert(`Error: ${error.message || 'Failed to activate plan'}`);
        } else {
          Alert.alert('Error', error.message || 'Failed to activate plan');
        }
      } finally {
        setPurchasing(false);
      }
      return;
    }

    // Paid plans - use Stripe Checkout
    try {
      setPurchasing(true);

      const owner = await AuthManager.getSavedBusinessOwner();
      if (!owner) {
        throw new Error('Business owner not found. Please log in again.');
      }

      const businessId = owner.id || owner.email || '';
      if (!businessId) {
        throw new Error('Unable to identify business owner');
      }

      // Map package ID to subscription plan ID
      const planMap: { [key: string]: string } = {
        'free': 'free',
        'starter': 'starter',
        'professional': 'professional',
        'business': 'business'
      };

      const planId = planMap[selectedPackage] || 'free';

      // For web, go directly to Stripe Checkout (no payment intent needed)
      // Stripe Checkout handles everything: Apple Pay, Google Pay, Cards
      try {
        // Create Stripe Checkout session and redirect
        console.log('🔍 PackageSelectionScreen: Creating Stripe Checkout session...', {
          planId: planId,
          businessId: businessId,
          businessEmail: owner.email,
          API_BASE_URL
        });
        
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        let checkoutResponse: Response;
        try {
          console.log('🔍 PackageSelectionScreen: Sending fetch request...');
          checkoutResponse = await fetch(`${API_BASE_URL}/api/product-subscriptions/checkout/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              planId: planId,
              businessId: businessId,
              businessEmail: owner.email || '',
              businessName: owner.name || owner.businessName || 'Business',
              successUrl: Platform.OS === 'web' 
                ? `${window.location.origin}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}`
                : `bookapt://subscription?success=true&session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: Platform.OS === 'web'
                ? `${window.location.origin}/subscription?canceled=true`
                : `bookapt://subscription?canceled=true`,
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          console.log('✅ PackageSelectionScreen: Fetch completed, status:', checkoutResponse.status);
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          console.error('❌ PackageSelectionScreen: Fetch error:', fetchError);
          if (fetchError.name === 'AbortError') {
            throw new Error('Request timed out. The server may be slow or unreachable. Please try again or contact support.');
          }
          throw fetchError;
        }

        console.log('🔍 PackageSelectionScreen: Checkout response status:', checkoutResponse.status);

        if (!checkoutResponse.ok) {
          const errorText = await checkoutResponse.text();
          let errorMessage = `Server error: ${checkoutResponse.status}`;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const responseText = await checkoutResponse.text();
        console.log('🔍 PackageSelectionScreen: Response text:', responseText);
        
        let checkoutData;
        try {
          checkoutData = JSON.parse(responseText);
          console.log('🔍 PackageSelectionScreen: Checkout response data:', checkoutData);
        } catch (parseError) {
          console.error('❌ PackageSelectionScreen: Failed to parse JSON:', parseError);
          console.error('❌ Response text was:', responseText);
          throw new Error(`Invalid response from server: ${responseText.substring(0, 100)}`);
        }
        
        if (checkoutData.success && checkoutData.sessionUrl) {
          // Redirect to Stripe Checkout
          console.log('✅ PackageSelectionScreen: Redirecting to Stripe Checkout:', checkoutData.sessionUrl);
          console.log('✅ PackageSelectionScreen: About to redirect...');
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.location.href = checkoutData.sessionUrl;
            console.log('✅ PackageSelectionScreen: Redirect initiated');
            // Don't set purchasing to false here - let the redirect happen
            return;
          } else {
            throw new Error('Cannot redirect on non-web platform');
          }
        } else {
          const errorMsg = checkoutData.error || 'Failed to create checkout session';
          console.error('❌ PackageSelectionScreen: Checkout session creation failed:', errorMsg);
          console.error('❌ Full response:', checkoutData);
          throw new Error(errorMsg);
        }
      } catch (checkoutError: any) {
        console.error('❌ PackageSelectionScreen: Checkout error:', checkoutError);
        console.error('❌ Error details:', {
          message: checkoutError.message,
          stack: checkoutError.stack,
          name: checkoutError.name
        });
        
        setPurchasing(false);
        
        // Detect CORS errors and provide helpful message
        const isCorsError = checkoutError.message?.includes('CORS') || 
                           checkoutError.message?.includes('Failed to fetch') ||
                           checkoutError.message?.includes('Access-Control-Allow-Origin') ||
                           checkoutError.message?.includes('NetworkError') ||
                           checkoutError.message?.includes('Network request failed');
        
        const isTimeoutError = checkoutError.message?.includes('timed out') || 
                              checkoutError.message?.includes('timeout');
        
        let errorMessage: string;
        if (isTimeoutError) {
          errorMessage = checkoutError.message + '\n\n' +
            'Possible causes:\n' +
            '- Backend server is not responding\n' +
            '- Network connection issue\n' +
            '- CORS configuration blocking the request\n\n' +
            'Please try again or contact support if the problem persists.';
        } else if (isCorsError) {
          const currentOrigin = Platform.OS === 'web' && typeof window !== 'undefined' 
            ? window.location.origin 
            : 'your app';
          errorMessage = 'Unable to connect to payment service.\n\n' +
            'This is likely a CORS (Cross-Origin Resource Sharing) issue.\n\n' +
            'The backend API needs to allow requests from: ' + currentOrigin + '\n\n' +
            'Please contact support or try again later.';
        } else {
          errorMessage = checkoutError.message || 'Failed to create checkout session. Please try again or contact support.';
        }
        
        if (Platform.OS === 'web') {
          window.alert(errorMessage);
        } else {
          Alert.alert('Payment Error', errorMessage);
        }
      }
    } catch (error: any) {
      console.error('❌ PackageSelectionScreen: Error in handleWebPurchase:', error);
      setPurchasing(false);
      if (Platform.OS === 'web') {
        window.alert(`Error: ${error.message || 'Failed to process subscription. Please try again.'}`);
      } else {
        Alert.alert('Error', error.message || 'Failed to process subscription. Please try again.');
      }
    }
  };


  // Check for Founding Member signup (from webapp redirect)
  const [isFoundingMember, setIsFoundingMember] = React.useState(false);
  
  React.useEffect(() => {
    // In webapp only: check URL parameters (window.location is undefined on React Native)
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const fm = urlParams.get('foundingMember') === 'true';
      if (fm) {
        setIsFoundingMember(true);
      }
    }
    // Also check localStorage (web only; not available on mobile)
    if (Platform.OS === 'web') {
      try {
        const savedSignup = localStorage.getItem('foundingMemberSignup');
        if (savedSignup) {
          setIsFoundingMember(true);
        }
      } catch (e) {
        // Not in web environment
      }
    }
  }, []);

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Choose Your Plan</Text>
        <Text style={styles.subtitle}>Select the package that best fits your business needs</Text>
      </View>

      {/* Founding Member Banner */}
      {isFoundingMember && (
        <View style={styles.foundingMemberBanner}>
          <Text style={styles.foundingMemberTitle}>🎉 You're a Founding Member!</Text>
          <Text style={styles.foundingMemberText}>
            Get 50% off your first year and lock in this price for 5 years.
            Limited to first 200 businesses.
          </Text>
          <Text style={styles.foundingMemberSubtext}>
            Scroll down to see Founding Member plans
          </Text>
        </View>
      )}

      <View style={styles.packagesContainer}>
        {packages.map((pkg) => (
          <ModernCard
            key={pkg.id}
            style={[
              styles.packageCard,
              selectedPackage === pkg.id && styles.selectedPackage,
              pkg.popular && styles.popularPackage,
            ]}
            onPress={() => handleSelectPackage(pkg.id)}
          >
            {pkg.popular && (
              <View style={styles.popularBadge}>
                <Text style={styles.popularText}>Most Popular</Text>
              </View>
            )}

            <View style={styles.packageHeader}>
              <Text style={styles.packageName}>{pkg.name}</Text>
              <View style={styles.priceContainer}>
                <Text style={styles.price}>${pkg.price}</Text>
                <Text style={styles.pricePeriod}>/month</Text>
              </View>
            </View>

            <View style={styles.featuresContainer}>
              {pkg.features.map((feature, index) => (
                <View key={index} style={styles.featureItem}>
                  <Text style={styles.featureIcon}>✓</Text>
                  <Text style={styles.featureText}>{feature}</Text>
                </View>
              ))}
            </View>

            {selectedPackage === pkg.id && (
              <View style={styles.selectedIndicator}>
                <Text style={styles.selectedText}>Selected</Text>
              </View>
            )}
          </ModernCard>
        ))}
      </View>

      <View style={styles.buttonsWrapper}>
        {!isWeb && RNIap && getAvailablePurchases && (
          <TouchableOpacity
            onPress={handleRestorePurchases}
            disabled={restoring}
            style={[styles.restoreButton, restoring ? styles.restoreButtonDisabled : null]}
          >
            {restoring ? (
              <ActivityIndicator size="small" color="#007AFF" />
            ) : (
              <Text style={styles.restoreButtonText}>Restore Purchases</Text>
            )}
          </TouchableOpacity>
        )}
        <View style={styles.buttonContainer}>
          <ModernButton
          title="Back"
          variant="outline"
          onPress={onBack}
          style={styles.backButton}
        />
        <ModernButton
          title={
            Platform.OS !== 'web' && selectedPackage && selectedPackage !== 'free'
              ? 'Subscribe'
              : 'Continue'
          }
          variant="primary"
          onPress={handleContinue}
          disabled={!selectedPackage}
          style={styles.continueButton}
        />
        </View>
      </View>

    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#007AFF',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  packagesContainer: {
    padding: 20,
  },
  packageCard: {
    marginBottom: 16,
    position: 'relative',
    borderWidth: 2,
    borderColor: '#E1E5E9',
  },
  selectedPackage: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  popularPackage: {
    borderColor: '#FF9500',
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    right: 20,
    backgroundColor: '#FF9500',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    zIndex: 1,
  },
  popularText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  packageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  packageName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  price: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  pricePeriod: {
    fontSize: 16,
    color: '#8E8E93',
    marginLeft: 4,
  },
  featuresContainer: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureIcon: {
    fontSize: 16,
    color: '#34C759',
    marginRight: 12,
    fontWeight: 'bold',
  },
  featureText: {
    fontSize: 14,
    color: '#1C1C1E',
    flex: 1,
  },
  selectedIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    backgroundColor: '#007AFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  selectedText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  buttonsWrapper: {
    padding: 20,
  },
  restoreButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  restoreButtonDisabled: {
    opacity: 0.6,
  },
  restoreButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  backButton: {
    flex: 1,
  },
  continueButton: {
    flex: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 40,
    maxHeight: '90%',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
    color: '#1C1C1E',
  },
  modalSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: '#8E8E93',
    marginBottom: 24,
  },
  subscriptionOption: {
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#E1E5E9',
  },
  recommendedOption: {
    borderColor: '#4CAF50',
    backgroundColor: '#F1F8F4',
    position: 'relative',
  },
  recommendedBadgeModal: {
    position: 'absolute',
    top: -10,
    right: 20,
    backgroundColor: '#4CAF50',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  recommendedTextModal: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
  },
  optionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1C1C1E',
  },
  optionPrice: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#007AFF',
    marginBottom: 8,
  },
  priceContainerModal: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  originalPriceModal: {
    fontSize: 20,
    color: '#8E8E93',
    textDecorationLine: 'line-through',
    marginRight: 8,
  },
  discountedPriceModal: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  savingsBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#4CAF50',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    marginBottom: 8,
  },
  savingsText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  optionDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  skipButton: {
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  skipButtonText: {
    color: '#8E8E93',
    fontSize: 16,
  },
  closeButton: {
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  closeButtonText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: '600',
  },
  foundingMemberBanner: {
    backgroundColor: '#667eea',
    padding: 20,
    margin: 20,
    marginTop: 0,
    borderRadius: 12,
    alignItems: 'center',
  },
  foundingMemberTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  foundingMemberText: {
    color: '#FFFFFF',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 20,
  },
  foundingMemberSubtext: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  paymentButtonDisabled: {
    opacity: 0.6,
  },
  purchasingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    gap: 8,
  },
  purchasingText: {
    fontSize: 14,
    color: '#007AFF',
  },
});

export default PackageSelectionScreen;